import { Map, MapPin, Navigation } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface RegionMapWidgetProps {
  currentRegion: string | null;
  visitedRegions: string[] | null;
  regionConnections: Record<string, string[] | { type?: string; connections?: string[] }> | null;
  compact?: boolean;
}

export function RegionMapWidget({ currentRegion, visitedRegions, regionConnections, compact }: RegionMapWidgetProps) {
  const region = currentRegion || "Unknown";
  const visited = visitedRegions || [];
  const connections = regionConnections || {};
  const rawExits = connections[region];
  const exits: string[] = Array.isArray(rawExits) ? rawExits : (rawExits?.connections || []);

  if (compact) {
    return (
      <div className="flex items-center gap-1 text-muted-foreground">
        <MapPin className="w-3 h-3 text-primary" />
        <span className="text-foreground font-medium truncate max-w-[100px]">{region}</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Map className="w-5 h-5 text-primary" />
        <span className="text-sm font-semibold text-foreground">Current Region</span>
      </div>

      <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-primary" />
          <span className="font-medium text-foreground">{region}</span>
        </div>
      </div>

      {exits.length > 0 && (
        <div className="space-y-2">
          <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest flex items-center gap-1">
            <Navigation className="w-3 h-3" /> Exits
          </div>
          <div className="flex flex-wrap gap-1.5">
            {exits.map((exit) => (
              <Badge key={exit} variant="outline" className="text-xs">
                {exit}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {visited.length > 0 && (
        <div className="space-y-2">
          <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
            Visited ({visited.length})
          </div>
          <div className="flex flex-wrap gap-1">
            {visited.map((r) => (
              <span key={r} className={`text-[11px] px-2 py-0.5 rounded-md ${r === region ? 'bg-primary/15 text-primary font-medium' : 'bg-muted/30 text-muted-foreground'}`}>
                {r}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function RegionStatusWidget({ currentRegion, regionType, compact }: { currentRegion: string | null; regionType: string | null; compact?: boolean }) {
  if (!currentRegion) return null;
  return (
    <div className="flex items-center gap-1 text-muted-foreground">
      <MapPin className="w-3 h-3 text-primary" />
      <span className="text-foreground font-medium truncate max-w-[100px]">{currentRegion}</span>
      {regionType && <span className="text-[10px] opacity-60">({regionType})</span>}
    </div>
  );
}
